import React, { useState } from "react";

import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { patchTranslateReportToEnglish } from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

interface TranslateDialogProps {
  open: boolean;
  onClose: () => void;
  submissionId: string;
  onTranslated: () => Promise<void> | void;
}

const TranslateDialog: React.FC<TranslateDialogProps> = ({
  open,
  onClose,
  submissionId,
  onTranslated,
}) => {
  const { t } = useTranslation("reportsv2/condensed_report");
  const [isTranslating, setIsTranslating] = useState(false);

  const handleTranslate = async () => {
    try {
      setIsTranslating(true);
      const response = await patchTranslateReportToEnglish({
        path: { submission_id: submissionId },
      });

      if (response.data) {
        toast.success(
          `Translation successful! Detected language: ${response.data.detected_language || "Unknown"}`,
        );
        await onTranslated();
      }
    } catch (error) {
      console.error("Translation failed:", error);
      toast.error("Translation failed. Please try again.");
    } finally {
      setIsTranslating(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t("translate")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will automatically detect the language of the report content and
          translate it to English. The translation will be stored and displayed
          immediately.
        </DialogContentText>
      </DialogContent>
      <DialogActions
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px",
        }}
      >
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={isTranslating}
        >
          {t("cancel")}
        </Button>
        <LoadingButton
          onClick={handleTranslate}
          color="primary"
          variant="contained"
          loading={isTranslating}
          disabled={isTranslating}
        >
          {t("translate")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default TranslateDialog;
